//* react
import React from 'react';

//* material-ui
import {
  Autocomplete,
  Box,
  Button,
  createFilterOptions,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';

//* types
import type { ChatUserModel } from 'types/chat-users/chat-user';

//* system
import useLocalization from 'hooks/useLocalization';
import { matchIsValidTel, MuiTelInput, MuiTelInputInfo } from 'mui-tel-input';
import { store, useSelector } from 'store';
import TemplatesMenu from 'views/common/templates';
import { fetchSendTemplateMessage, FetchSendTemplateMessageParams } from 'api/templates';
import { phoneRegex, internationalPhoneRegex } from 'utils/regexes';
import { createChatUser, getChatUserByPhoneNumber } from 'api/chat-users';
import { getChatUsers } from 'store/slices/chat';
import { useSnackbar } from 'notistack';
import { CHAT_LIST_PANEL_WIDTH_PX } from 'layout/MainLayout/layout-zoom-constants';
// import { closeAppSidebar, updateSidebar } from 'store/slices/app-sidebar';
import { SelectedTemplateChangeParams } from 'types/templates/templates-menu';

function contactPrimaryLabel(u: ChatUserModel): string {
  const n =
    (u.FullName && u.FullName.trim()) ||
    (u.ComposedName && u.ComposedName.trim()) ||
    (u.ContactFullName && u.ContactFullName.trim()) ||
    (u.WhatsappFullName && u.WhatsappFullName.trim()) ||
    '';
  return n || u.UserDisplayPhoneNumber || '';
}

function ilLocalDigitsFromStored(stored: string): string {
  const d = stored.replace(/\D/g, '');
  if (d.startsWith('972') && d.length >= 11) return `0${d.slice(3)}`;
  return d;
}

function intlPlusFromStored(stored: string): string {
  const d = stored.replace(/\D/g, '');
  if (!d) return '';
  return `+${d}`;
}

interface SendQuickTemplateSidebarProps {
  open: boolean;
  onClose: () => void;
  /** מילוי טלפון מהחיפוש ברשימת השיחות */
  initialPhone?: string;
}

const SendQuickTemplateSidebar: React.FC<SendQuickTemplateSidebarProps> = ({ open, onClose, initialPhone }) => {
  const theme = useTheme();
  //* localization
  const { currentLocale } = useLocalization();
  const localization = React.useMemo(() => currentLocale.chat.chatHeader.sendTemplateForm, [currentLocale]);

  //* data & states
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const chatUsersFromStore = useSelector((state) => state.chat.chatUsers.allChatUsers) as ChatUserModel[];
  /** כמו פאנל התבניות בצד חלון השיחה (`RightAppSidebar` / `dualSidebar.right.width`) */
  const templatesPanelWidthPx = useSelector(
    (state) => state.dualSidebar.right.width ?? CHAT_LIST_PANEL_WIDTH_PX,
  );
  /** רוחב תוכן הדיאלוג — מעט רחב יותר מפאנל התבניות לתחושה מאווררת */
  const dialogContentWidthPx = React.useMemo(
    () => Math.round(templatesPanelWidthPx * 1.12) + 24,
    [templatesPanelWidthPx],
  );
  /** רוחב נייר הדיאלוג ≈ רוחב התוכן + px של DialogContent */
  const dialogPaperMaxWidthPx = React.useMemo(() => {
    const parsePx = (v: string | number) => (typeof v === 'number' ? v : parseFloat(v));
    const contentPadX = parsePx(theme.spacing(3.5)) * 2;
    return dialogContentWidthPx + contentPadX;
  }, [theme, dialogContentWidthPx]);
  const filterContactOptions = React.useMemo(
    () =>
      createFilterOptions<ChatUserModel>({
        matchFrom: 'any',
        stringify: (u) =>
          [
            u.FullName,
            u.ComposedName,
            u.ContactFullName,
            u.WhatsappFullName,
            u.UserDisplayPhoneNumber,
            u.SearchIndexName,
            u.NotificationContactName,
          ]
            .filter(Boolean)
            .join(' '),
      }),
    [],
  );
  const companyPhoneConfigCountryCode = useSelector(
    (state) => state.companyPlatforms.companyPhoneConfig?.countryCode || 'IL',
  );

  const [sendTemplatePhone, setSendTemplatePhone] = React.useState<string>('');
  const [sendTemplateName, setSendTemplateName] = React.useState<string>('');
  // const [selectedTemplate, setSelectedTemplate] = React.useState<{} | null>(null);
  const [isTemplateValid, setIsTemplateValid] = React.useState<boolean>(false);
  const [phoneError, setPhoneError] = React.useState<string | null>(null);

  const selectedTemplateRef = React.useRef<FetchSendTemplateMessageParams | null>(null);
  const phoneCountryCodeRef = React.useRef<MuiTelInputInfo['countryCode'] | null>();

  //* handlers
  const handleClose = () => {
    // closeAppSidebar();
    onClose();
    setTimeout(() => {
      setSendTemplatePhone('');
      setSendTemplateName('');
      handleSelectedTemplate(null);
      setIsTemplateValid(false);
    }, 300);
  };

  React.useEffect(() => {
    if (!open) return;
    const raw = initialPhone?.trim();
    if (!raw) return;
    if (companyPhoneConfigCountryCode === 'IL') {
      setSendTemplatePhone(raw.replace(/\D/g, ''));
    } else {
      setSendTemplatePhone(raw);
    }
    setPhoneError(null);
  }, [open, initialPhone, companyPhoneConfigCountryCode]);
  const handlePhoneNumber = (value: string) => {
    if (value !== '' && !internationalPhoneRegex.test(value)) setPhoneError('ניתן להזין ספרות בלבד');
    else setPhoneError(null);
    setSendTemplatePhone(value.replaceAll(/\D/g, ''));
  };
  const handlePhoneNumberWithCountry = (value: string, telInfo: MuiTelInputInfo) => {
    //* =============
    if (telInfo.reason === 'country') {
      phoneCountryCodeRef.current = telInfo.countryCode;
    }
    if (matchIsValidTel(value)) {
      setPhoneError(null);
    } else {
      setPhoneError('ניתן להזין ספרות בלבד');
    }

    setSendTemplatePhone(value);
  };

  const onSelectedTemplateChange = React.useCallback(
    ({ valid, sendObject }: SelectedTemplateChangeParams) => {
      // if (isValid) handleSelectedTemplate(sendObject);
      // else handleSelectedTemplate(null);
      setIsTemplateValid(valid);
      handleSelectedTemplate(sendObject);
    },
    [],
  );
  const handleSelectedTemplate = (template: FetchSendTemplateMessageParams | null) => {
    // setSelectedTemplate(template);
    selectedTemplateRef.current = template;
    console.log(template);
  };

  const onSubmitForm = async () => {
    try {
      // setTemplateSending(true);
      if (!selectedTemplateRef.current) return;
      let sendTemplateObject = { ...selectedTemplateRef.current };

      let targetPhone = phoneRegex.test(sendTemplatePhone)
        ? '972' + sendTemplatePhone.slice(1)
        : sendTemplatePhone.replaceAll(/\D/g, '');

      handleClose();
      const infoAlert = enqueueSnackbar(localization.sending, {
        variant: 'info',
      });

      let storeState = store.getState();
      const chatUsers = storeState.chat.chatUsers;
      let existingChatUser = chatUsers.allChatUsers.find(
        (chatUser) => chatUser.UserDisplayPhoneNumber === targetPhone,
      );
      if (!existingChatUser) {
        const existingChatUserRequest = await getChatUserByPhoneNumber(targetPhone);
        if (existingChatUserRequest) {
          existingChatUser = existingChatUserRequest.contact[0];
        }
      }

      if (!existingChatUser) {
        const createUserParams = {
          firstName: sendTemplateName.split(' ')[0],
          lastName: sendTemplateName.split(' ').length > 1 ? sendTemplateName.split(' ')[1] : '',
          phone: targetPhone,
          isFireberry: false,
          // phoneType: selectedPhoneNumberType,
        };

        console.log('create user params: ', createUserParams);
        console.log('target phone: ', targetPhone);
        const data = await createChatUser(
          createUserParams.firstName,
          createUserParams.lastName,
          createUserParams.phone,
          '',
        );
        
        if (data && 'Id' in data) {
          console.log('create user data: ', data);
          existingChatUser = data;
          //TODO add user according to filters instead of requesting all users again
          await getChatUsers(false);
        } else {
          return enqueueSnackbar(localization.userCreateErr, { variant: 'error' });
        }
      }

      console.log('existingChatUser:', existingChatUser);
      console.log('sendTemplateObject before processing:', sendTemplateObject);

      // פונקציה פשוטה להחלפת משתנים
      const replaceVariables = (text: string): string => {
        if (!text || !existingChatUser) return text;
        
        console.log('Replacing variables in text:', text);
        console.log('Using contact data:', {
          firstName: existingChatUser.FirstName,
          lastName: existingChatUser.LastName,
          fullName: existingChatUser.FullName,
          phone: existingChatUser.UserDisplayPhoneNumber,
          email: existingChatUser.Email
        });
        
        const firstName = existingChatUser.FirstName?.length>0? existingChatUser.FirstName : existingChatUser.FullName;
        const lastName = existingChatUser.LastName?.length>0? existingChatUser.LastName : existingChatUser.FullName;
        const fullName = existingChatUser.FullName || '';
        const phone = existingChatUser.UserDisplayPhoneNumber || '';
        const email = existingChatUser.Email || '';

        let result = text
          .replace(/\$contact\.firstName/g, firstName)
          .replace(/\$contact\.lastName/g, lastName)
          .replace(/\$contact\.accountname/g, fullName)
          .replace(/\$contact\.phone/g, phone)
          .replace(/\$contact\.email/g, email);
          
        console.log('Text after variable replacement:', result);
        return result;
      };

      // החלפת משתנים בטמפלייט - עבודה עם המבנה החדש
      const componentsParsed = JSON.parse(sendTemplateObject.message);
      
      // החלפת משתנים ב-components array
      console.log('componentsParsed structure:', componentsParsed);
      
      // הקוד הקודם חיפש componentsParsed.components, אבל המבנה הוא componentsParsed.BODY
      if (componentsParsed.BODY && componentsParsed.BODY.text) {
        console.log('Original BODY text:', componentsParsed.BODY.text);
        componentsParsed.BODY.text = replaceVariables(componentsParsed.BODY.text);
        console.log('Replaced BODY text:', componentsParsed.BODY.text);
      }
      
      // גם נבדוק אם יש עוד סוגי קומפוננטים
      Object.keys(componentsParsed).forEach(key => {
        const component = componentsParsed[key];
        console.log(`Processing component ${key}:`, component);
        
        if (component && typeof component === 'object') {
          // Handle text replacement
          if (component.text) {
            console.log(`Original ${key} text:`, component.text);
            component.text = replaceVariables(component.text);
            console.log(`Replaced ${key} text:`, component.text);
          }
        }
      });
      
      console.log('Components after variable replacement:', componentsParsed);

      // Update to new structure
      storeState = store.getState();
      
      // Update components array with variable replacements
      const updatedComponentsArray = sendTemplateObject.components.map((comp: any) => {
        const updatedComp = { ...comp };
        if (updatedComp.parameters) {
          updatedComp.parameters = updatedComp.parameters.map((param: any) => {
            if (param.text) {
              return { ...param, text: replaceVariables(param.text) };
            }
            return param;
          });
        }
        return updatedComp;
      });

      // Update sendTemplateObject to new structure
      sendTemplateObject.id = targetPhone;
      sendTemplateObject.message = JSON.stringify(componentsParsed);
      sendTemplateObject.components = updatedComponentsArray;
      
      // Final validation for file parameters
      const finalComponents = componentsParsed;
      
      console.log('=== FINAL VALIDATION ===');
      console.log('Final components:', finalComponents);
      console.log('Final components array:', updatedComponentsArray);
      
      // Check if file parameters are present and have links
      let hasFileParameters = false;
      let hasMissingFileLinks = false;
      
      updatedComponentsArray.forEach((comp: any) => {
        if (comp.parameters) {
          comp.parameters.forEach((param: any) => {
            if (param.type === 'document' || param.type === 'image' || param.type === 'video') {
              hasFileParameters = true;
              console.log('Found file parameter:', param);
              
              // Check if file link is missing
              const hasLink = (param.type === 'document' && param.document?.link) ||
                             (param.type === 'image' && param.image?.link) ||
                             (param.type === 'video' && param.video?.link);
              
              if (!hasLink) {
                hasMissingFileLinks = true;
              }
            }
          });
        }
      });
      
      if (hasFileParameters) {
        console.log('✅ Template contains file parameters');
        if (hasMissingFileLinks) {
          console.log('⚠️ Some file parameters are missing links');
          enqueueSnackbar('ההודעה תופיע בשיחה אך לא תישלח בפועל מכיוון שלא צורף מסמך לתבנית', {
            variant: 'warning',
            autoHideDuration: 6000
          });
        }
      } else {
        console.log('ℹ️ Template does not contain file parameters');
      }
      
      console.log('Final send template object: ', sendTemplateObject);
      console.log('Final components: ', sendTemplateObject.components);
      const data = await fetchSendTemplateMessage(sendTemplateObject);
      if (data?.result) {
        closeSnackbar(infoAlert);
        // Don't show success message if there are missing file links
        if (!hasMissingFileLinks) {
          enqueueSnackbar(localization.sendSuccess, { variant: 'success' });
        }
        await getChatUsers(false)
      } else {
        closeSnackbar(infoAlert);
        enqueueSnackbar(localization.sendErr, { variant: 'error' });
      }
    } catch (error) {
      console.log('submit send template error: ', error);
      enqueueSnackbar(localization.sendErr, { variant: 'error' });
    } finally {
      // setTemplateSending(false);
    }
  };

  // React.useEffect(() => {
  //   updateSidebar({
  //     title: localization.title,
  //   });
  // }, [localization.title]);

  const applyContactFromStore = (u: ChatUserModel) => {
    setSendTemplateName(contactPrimaryLabel(u));
    if (companyPhoneConfigCountryCode === 'IL') {
      handlePhoneNumber(ilLocalDigitsFromStored(u.UserDisplayPhoneNumber));
    } else {
      const plus = intlPlusFromStored(u.UserDisplayPhoneNumber);
      handlePhoneNumberWithCountry(plus, {
        reason: 'input',
        countryCode: phoneCountryCodeRef.current ?? companyPhoneConfigCountryCode,
      } as MuiTelInputInfo);
    }
  };

  const loc = localization as typeof localization & {
    contactPickerPlaceholder?: string;
    contactPickFromSystem?: string;
    sectionRecipient?: string;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth={false}
      maxWidth={false}
      PaperProps={{
        sx: {
          m: { xs: 2, sm: 3 },
          boxSizing: 'border-box',
          width: { xs: 'calc(100vw - 16px)', sm: 'max-content' },
          maxWidth: {
            xs: 'calc(100vw - 16px)',
            sm: `min(calc(100vw - 48px), ${dialogPaperMaxWidthPx}px)`,
          },
          alignSelf: 'center',
        },
      }}
    >
      <DialogTitle
        sx={(t) => ({
          px: t.spacing(3.5),
          pt: t.spacing(2),
          pb: t.spacing(1),
        })}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
          <SendOutlinedIcon color="primary" sx={{ fontSize: theme.spacing(3.5), flexShrink: 0 }} aria-hidden />
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, lineHeight: 1.25 }}>
            {localization.title}
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent
        sx={(t) => ({
          height: '68vh',
          maxHeight: '68vh',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          px: t.spacing(3.5),
          pt: t.spacing(1.5),
          pb: t.spacing(2.5),
        })}
      >
        <Stack
          spacing={2.5}
          sx={{
            flex: 1,
            minHeight: 0,
            height: '100%',
            width: { xs: '100%', sm: dialogContentWidthPx },
            maxWidth: '100%',
          }}
        >
          <Stack spacing={1.5} sx={{ flexShrink: 0, width: '100%' }}>
            {companyPhoneConfigCountryCode !== 'IL' && (
              <Autocomplete
                options={chatUsersFromStore}
                filterOptions={filterContactOptions}
                getOptionLabel={(u) => `${contactPrimaryLabel(u)} · ${u.UserDisplayPhoneNumber}`}
                onChange={(_, value) => {
                  if (value && typeof value === 'object') {
                    applyContactFromStore(value);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={loc.contactPickFromSystem ?? loc.sectionRecipient}
                    placeholder={loc.contactPickerPlaceholder}
                    size="small"
                  />
                )}
                slotProps={{
                  listbox: {
                    sx: { maxHeight: 220 },
                  },
                }}
              />
            )}

            <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
              <TextField
                value={sendTemplateName}
                onChange={(e) => setSendTemplateName(e.target.value)}
                type="text"
                required
                label={localization.nameLabel}
                variant="outlined"
                sx={{ flex: '1 1 0', minWidth: 0 }}
              />
              {companyPhoneConfigCountryCode === 'IL' ? (
                <Autocomplete
                  freeSolo
                  options={chatUsersFromStore}
                  filterOptions={filterContactOptions}
                  inputValue={sendTemplatePhone}
                  onInputChange={(_, newInput, reason) => {
                    if (reason === 'reset') return;
                    if (reason === 'input' || reason === 'clear') {
                      handlePhoneNumber(newInput);
                    }
                  }}
                  onChange={(_, value) => {
                    if (value && typeof value === 'object') {
                      applyContactFromStore(value);
                    }
                  }}
                  getOptionLabel={(option) =>
                    typeof option === 'string'
                      ? option
                      : `${contactPrimaryLabel(option)} · ${ilLocalDigitsFromStored(option.UserDisplayPhoneNumber)}`
                  }
                  isOptionEqualToValue={(a, b) =>
                    typeof a !== 'string' && typeof b !== 'string' && a.Id === b.Id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={localization.phoneLabel}
                      placeholder={loc.contactPickerPlaceholder}
                      variant="outlined"
                      error={Boolean(phoneError)}
                      helperText={phoneError}
                    />
                  )}
                  slotProps={{
                    listbox: {
                      sx: { maxHeight: 220 },
                    },
                  }}
                  sx={{ flex: '1 1 0', minWidth: 0 }}
                />
              ) : (
                <MuiTelInput
                  value={sendTemplatePhone}
                  onChange={(newValue, info) => handlePhoneNumberWithCountry(newValue, info)}
                  label={localization.phoneLabel}
                  variant="outlined"
                  error={Boolean(phoneError)}
                  helperText={phoneError}
                  defaultCountry={companyPhoneConfigCountryCode}
                  forceCallingCode={true}
                  dir="ltr"
                  sx={{ flex: '1 1 0', minWidth: 0 }}
                />
              )}
            </Stack>
          </Stack>

          <Stack spacing={0.75} sx={{ flex: '1 1 0', minHeight: 0, overflow: 'hidden' }}>
            <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 600, flexShrink: 0 }}>
              {loc.sectionTemplates ?? localization.title}
            </Typography>
            <Box
              sx={{
                flex: '1 1 0',
                minHeight: 0,
                overflow: 'hidden',
                width: '100%',
                boxSizing: 'border-box',
              }}
            >
              <TemplatesMenu
                sendable={false}
                chatUser={null}
                closable={false}
                onSelectedTemplateChange={onSelectedTemplateChange}
                disablePadding
                compact
                scrollableListLayout
                templateListScrollMode="inner"
              />
            </Box>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions
        sx={(t) => ({
          px: t.spacing(3.5),
          py: t.spacing(1.75),
          gap: t.spacing(1.25),
          minHeight: 0,
        })}
      >
        <Button variant="outlined" color="primary" onClick={handleClose}>
          {localization.close}
        </Button>
        <Button
          variant="contained"
          color="primary"
          // disabled={!sendTemplatePhone.length || !selectedTemplate || templateSending}
          disabled={!sendTemplatePhone.length || !sendTemplateName.trim() || !isTemplateValid || Boolean(phoneError)}
          onClick={() => onSubmitForm()}
          sx={{
            position: 'relative',
          }}
        >
          {localization.send}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendQuickTemplateSidebar;
